import { PriceType } from '../../../../domain/price-response/interface';
import { PriceResponseMarket } from './PriceResponseMarket';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { startOfDay } from 'date-fns';

const HOUR_SEC = 60 * 60;
const DAY_MS = 24 * HOUR_SEC * 1000;

export interface IPricePoint {
    timestamp: number; // epoch ms
    price: number;
}

export interface IEvent {
    startTime: number; // epoch ms
    endTime: number; // epoch ms
}

export interface MonthStats {
    eventCount: number;
    eventDays: number;
    eventHours: number;
    savings: number;
}

export type EventsByYearMonth = Record<number, Record<number, MonthStats>>;

export interface IYearlyTotals {
    key: number | string;
    year: number | string;
    eventDays: number;
    eventHours: number;
    savings: number;
    total: number;
    isTotalRow?: boolean; // true for the aggregate "All Years" row, useful for styling in the table
}

export interface ICalculateEventsInput {
    prices: [number, number, number][];
    priceType: PriceType;
    triggerPrice: number;
    eventDurationHours: number;
    stopIfPriceDrops: boolean;
    cooldownHours: number;
    mw: number;
    maxDispatchesPerDay?: number; // 0 or undefined = unlimited
    intervalMinutes?: number;
    market: PriceResponseMarket;
}

export interface ICalculateEventsResult {
    eventsWithCap: IEvent[];
    eventsWithoutCap: IEvent[];
    summaryWithCap: EventsByYearMonth;
    summaryWithoutCap: EventsByYearMonth;
}

export function extractPriceSeries(prices: [number, number, number][], priceType: PriceType): IPricePoint[] {
    const priceIndex = priceType === PriceType.REAL_TIME ? 1 : 2;

    return prices
        .filter(row => row?.[priceIndex] !== null && row?.[priceIndex] !== undefined)
        .map(row => ({ timestamp: row[0] * 1000, price: row[priceIndex] }))
        .sort((a, b) => a.timestamp - b.timestamp);
}

function getStartOfDayTimestamp(timestamp: number, timezone: string): number {
    const zoned = toZonedTime(timestamp, timezone);
    const zonedStartOfDay = startOfDay(zoned);
    return fromZonedTime(zonedStartOfDay, timezone).valueOf();
}

/**
 * Single forward pass:
 * - starts an event when price > trigger and nothing is currently open/suppressed
 * - closes the open event early if stopIfPriceDrops and price falls back to <= trigger
 * - otherwise closes it naturally once eventDurationHours elapses
 * - next event can't start until (lastEvent.endTime + cooldownHours)
 * - if applyMaxDispatchesPerDay, also requires < maxDispatchesPerDay events in the trailing 24h window ending at the candidate start time
 */
function buildEvents(
    series: IPricePoint[],
    {
        triggerPrice,
        eventDurationHours,
        stopIfPriceDrops,
        cooldownHours,
        maxDispatchesPerDay,
    }: Pick<ICalculateEventsInput, 'triggerPrice' | 'eventDurationHours' | 'stopIfPriceDrops' | 'cooldownHours' | 'maxDispatchesPerDay'>,
    applyMaxDispatchesPerDay: boolean,
    timezone: string
): IEvent[] {
    const eventDurationMs = eventDurationHours * HOUR_SEC * 1000;
    const cooldownMs = cooldownHours * HOUR_SEC * 1000;

    const events: IEvent[] = [];
    let openEvent: IEvent | null = null;

    for (const point of series) {
        // Natural close: nominal duration elapsed
        if (openEvent && point.timestamp >= openEvent.endTime) {
            openEvent = null;
        }

        // Early close: price fell back under trigger while an event is active
        if (openEvent && stopIfPriceDrops && point.price < triggerPrice) {
            openEvent.endTime = point.timestamp;
            openEvent = null;
            continue;
        }

        if (point.price < triggerPrice) continue;
        if (openEvent) continue; // already inside an active window, not a new trigger

        // Cooldown vs. the most recently created event
        const lastEvent = events[events.length - 1];
        if (lastEvent && point.timestamp < lastEvent.endTime + cooldownMs) continue;

        // Calendar-day dispatch cap (market timezone), counted from start of day
        if (applyMaxDispatchesPerDay && maxDispatchesPerDay && maxDispatchesPerDay > 0) {
            const dayStart = getStartOfDayTimestamp(point.timestamp, timezone);
            const dayEnd = dayStart + DAY_MS;
            const dispatchesToday = events.filter(e => e.startTime >= dayStart && e.startTime < dayEnd).length;
            if (dispatchesToday >= maxDispatchesPerDay) continue;
        }

        const newEvent: IEvent = { startTime: point.timestamp, endTime: point.timestamp + eventDurationMs };
        events.push(newEvent);
        openEvent = newEvent;
    }

    return events;
}

function getYearMonthDayInTimezone(timestamp: number, timeZone: string): { year: number; month: number; day: number } {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
    }).formatToParts(new Date(timestamp));

    const get = (type: string) => Number(parts.find(p => p.type === type)?.value);
    return { year: get('year'), month: get('month') - 1, day: get('day') }; // month 0-indexed to match Date
}

/** Groups events by (year, month) in the market's timezone and computes Event Days/Hours/Savings */
function summarizeEvents(events: IEvent[], series: IPricePoint[], mw: number, timezone: string, intervalMinutes?: number): EventsByYearMonth {
    const detectedIntervalSec = series.length > 1 ? (series[1].timestamp - series[0].timestamp) / 1000 : 300;
    const intervalSec = intervalMinutes ? intervalMinutes * 60 : detectedIntervalSec;
    const intervalsPerHour = HOUR_SEC / intervalSec;

    const counts: EventsByYearMonth = {};
    const dayTrackers: Record<string, Set<string>> = {};

    let seriesIndex = 0; // moving pointer, never rewinds — series and events are both time-ordered

    for (const event of events) {
        // Advance past any points before this event's window starts
        while (seriesIndex < series.length && series[seriesIndex].timestamp < event.startTime) {
            seriesIndex++;
        }

        let priceSum = 0;
        let scanIndex = seriesIndex;

        while (scanIndex < series.length && series[scanIndex].timestamp < event.endTime) {
            priceSum += series[scanIndex].price;
            scanIndex++;
        }

        const { year, month, day } = getYearMonthDayInTimezone(event.startTime, timezone);

        if (!counts[year]) counts[year] = {};
        if (!counts[year][month]) counts[year][month] = { eventCount: 0, eventDays: 0, eventHours: 0, savings: 0 };

        const dayKey = `${year}-${month}`;
        if (!dayTrackers[dayKey]) dayTrackers[dayKey] = new Set();
        dayTrackers[dayKey].add(`${year}-${month}-${day}`);

        counts[year][month].eventCount += 1;
        counts[year][month].eventHours += (event.endTime - event.startTime) / (HOUR_SEC * 1000);
        counts[year][month].savings += (priceSum * mw) / intervalsPerHour;
    }

    for (const yearKey of Object.keys(counts)) {
        const year = Number(yearKey);
        for (const monthKey of Object.keys(counts[year])) {
            const month = Number(monthKey);
            counts[year][month].eventDays = dayTrackers[`${year}-${month}`]?.size ?? 0;
        }
    }

    return counts;
}

export function calculatePriceResponseScenariosEvents(input: ICalculateEventsInput): ICalculateEventsResult {
    const { prices, priceType, mw, intervalMinutes, market, ...eventParams } = input;
    const series = extractPriceSeries(prices, priceType);

    const eventsWithCap = buildEvents(series, eventParams, true, market.timezone);
    const eventsWithoutCap = buildEvents(series, eventParams, false, market.timezone);

    return {
        eventsWithCap,
        eventsWithoutCap,
        summaryWithCap: summarizeEvents(eventsWithCap, series, mw, market.timezone, intervalMinutes),
        summaryWithoutCap: summarizeEvents(eventsWithoutCap, series, mw, market.timezone, intervalMinutes),
    };
}

/** Aggregates monthly stats into yearly totals for the results table. Appends an "All Years"
 *  total row when there's more than one year. */
export function getYearlyTotals(events: EventsByYearMonth): IYearlyTotals[] {
    const years = Object.keys(events)
        .map(y => parseInt(y, 10))
        .sort((a, b) => a - b);

    const rows = years.map(year => {
        const monthStats = events[year] || {};
        const totals: IYearlyTotals = { key: year, year, eventDays: 0, eventHours: 0, savings: 0, total: 0 };

        for (let month = 0; month < 12; month++) {
            const stats = monthStats[month];
            if (!stats) continue;
            totals.eventDays += stats.eventDays;
            totals.eventHours += stats.eventHours;
            totals.savings += stats.savings;
            totals.total += stats.eventCount;
        }

        return totals;
    });

    if (rows.length > 1) {
        const grandTotal: IYearlyTotals = {
            key: 'total',
            year: 'Total',
            eventDays: 0,
            eventHours: 0,
            savings: 0,
            total: 0,
            isTotalRow: true,
        };

        for (const row of rows) {
            grandTotal.eventDays += row.eventDays;
            grandTotal.eventHours += row.eventHours;
            grandTotal.savings += row.savings;
            grandTotal.total += row.total;
        }

        rows.push(grandTotal);
    }

    return rows;
}
